import { Node, NodeProps } from "@xyflow/react"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { CircleHelp, Plus, Rocket, Trash } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActionTypeEnum, isActionConfigValid } from "schema"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { ActionIconMap } from "../maps/actionIconMap"
import { WorkflowNode } from "./WorkflowNode"
import { SendInternalEmailNode } from "./actions/SendInternalEmailNode"

export function ActionNode({ data, type, ...props }: NodeProps<Node>) {
  const { t } = useTranslation()
  const { onAddActionNode, onDeleteNode, setFocusedNode, onAddConditionNode } = useWorkflowBuilder()
  const [isOpen, setIsOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { form } = useWorkflowBuilder()

  const nodeIndex = useMemo(
    () => ensure(form.watch("nodes")?.findIndex((node) => node.id === props?.id)),
    [form, props.id],
  )
  const selectedAction = form.watch(
    `nodes.${nodeIndex}.action`,
  ) as (typeof ActionTypeEnum)[keyof typeof ActionTypeEnum]
  const selectedActionConfig = JSON.stringify(form.watch(`nodes.${nodeIndex}.config`))
  const message = form.watch(`nodes.${nodeIndex}.config.message`)

  const isComplete = useMemo(() => {
    if (!selectedAction) return false
    return isActionConfigValid(selectedAction, JSON.parse(selectedActionConfig))
  }, [selectedAction, selectedActionConfig])

  function handleAddConditionNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsOpen(false)
    setTimeout(() => {
      onAddConditionNode(props.id, "action")
    }, 100)
  }

  function handleAddActionNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsOpen(false)
    setTimeout(() => {
      onAddActionNode(props.id, "action")
    }, 100)
  }

  function onRequestDeleteNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsDeleteDialogOpen(true)
  }

  function onConfirmDeleteNode() {
    setIsDeleteDialogOpen(false)
    onDeleteNode(props.id)
  }

  function onCancelDeleteNode() {
    setIsDeleteDialogOpen(false)
    setFocusedNode(props.id)
  }

  const description = useMemo(() => {
    if (!selectedAction) return t("campaigns.action.title")

    const selectedActionLabel = t(`campaigns.action.availableActions.${selectedAction}.title`)
    const SelectedActionIcon = ActionIconMap[selectedAction]

    return (
      <>
        <div className="flex items-center gap-2">
          <SelectedActionIcon className="w-4 h-4 text-chart-primary" />
          <span className="text-base">{selectedActionLabel}</span>
        </div>
        <div>
          {selectedAction === "SendInConversationMessage" && (
            <span className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
              {message}
            </span>
          )}
          {selectedAction === "SendInternalEmail" && (
            <SendInternalEmailNode nodeIndex={nodeIndex} />
          )}
        </div>
      </>
    )
  }, [selectedAction, t, nodeIndex, message])

  return (
    <>
      <WorkflowNode
        {...props}
        data={data}
        type={type}
        labels={[
          <Badge key="action" variant="outline">
            <Rocket className="w-4 h-4" />
            {t("campaigns.action.label")}
          </Badge>,
        ]}
        isComplete={isComplete}
        description={description}
        addButton={
          <Popover open={isOpen} onOpenChange={setIsOpen} modal>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className={cn(
                  "absolute group-[.node-open]:border-chart-primary rounded-full -bottom-5 right-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 group-[.node-open]:opacity-100 transition-all",
                  isOpen && "opacity-100",
                )}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setIsOpen(true)
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="center" className="p-1">
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  onClick={handleAddConditionNode}
                  className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                >
                  <CircleHelp className="w-4 h-4 text-chart-primary" />
                  {t("campaigns.condition.label")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleAddActionNode}
                  className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                >
                  <Rocket className="w-4 h-4 text-chart-primary" />
                  {t("campaigns.action.label")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        }
        deleteButton={
          <Button
            size="icon"
            variant="ghost"
            onClick={onRequestDeleteNode}
            className="absolute w-8 h-8 right-1 bottom-1 hidden group-hover:flex group-[.node-open]:flex"
          >
            <Trash className="w-4 h-4" />
          </Button>
        }
      />
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("campaigns.action.deleteDialog.title")}</DialogTitle>
            <DialogDescription>{t("campaigns.action.deleteDialog.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onCancelDeleteNode}>
              {t("shared.cancel")}
            </Button>
            <Button variant="destructive" onClick={onConfirmDeleteNode}>
              {t("campaigns.action.deleteDialog.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
